import { useLayoutEffect } from 'react'
import { createExternalCss, createExternalScript } from '../Utils/CreateElement'

/**
 * Custom React hook to inject external JavaScript and CSS resources into the document.
 *
 * @param jsLink - The URL of the external JavaScript file to be injected. If null, no script will be injected.
 * @param cssLink - The URL of the external CSS file to be injected. If null, no stylesheet will be injected.
 */
export const useInjectExternalSources = (jsLink: Nullable<string>, cssLink: Nullable<string>): void => {
    /**
     * useLayoutEffect is used here to ensure that the external resources are injected before the browser paints the screen,
     * preventing any potential flash of unstyled content or issues with the external component trying to access its dependencies before they are loaded.
     */
    useLayoutEffect((): (() => void) | void => {
        const script: Nullable<HTMLScriptElement> = createExternalScript(jsLink)
        const css: Nullable<HTMLLinkElement> = createExternalCss(cssLink)

        if (script) document.body.appendChild(script)
        if (css) document.head.appendChild(css)

        return (): void => {
            if (script) document.body.removeChild(script)
            if (css) document.head.removeChild(css)
        }
    }, [jsLink, cssLink])
}
